// @flow
import React, { Fragment } from 'react';
import LedgerAccountsTable from './LedgerAccountsTable';
import ledgerCategories from './ledgerCategories';
import type { LedgerAccount, LedgerCategoryOrder } from '../types';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { useMutation } from '@apollo/client';
import styled from 'styled-components';
import { colors } from 'bench-style-constants';
import {
  TextBlock,
  Text,
  Button,
  Checkbox,
  toastError,
  checkboxStates,
} from 'bench-internal-theme';
import { showServerError } from 'bk-web-app';
import { FeatureValue, useFeatureFlags } from '@bench/ff-client';
// $FlowFixMe - TS file
import { SplitFeatureFlags } from '../../features';
import {
  SORT_LEDGER_CATEGORY_ALPHABETICAL,
  GET_LEDGER_CATEGORY_ORDER,
  SORT_LEDGER_CATEGORY_BY_ORDER_NUMBER,
} from '../queries';
import { useClient } from '../../ClientContext';

const LedgerAccountsTabLayout = styled.div`
  bottom: 0;
  left: 0;
  overflow: auto;
  padding: 32px;
  position: absolute;
  right: 0;
  top: 0;
`;

const LedgerAccountsTableHeaderLayout = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-direction: row;
`;

const SortingLayout = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-direction: row;
`;

const SortingCheckboxTextLayout = styled.div`
  padding-left: 8px;
`;

const PBDescriptionTextLayout = styled.div`
  margin-bottom: -15px;
  margin-top: 15px;
`;

type LedgerAccountsTabProps = {
  ledgerAccountsByCategory: {
    string: Array<LedgerAccount>,
  },
  search: string,
  isSorting: boolean,
  clearFiltersForSorting: () => void,
  canSort: boolean,
  ledgerCategoryOrders: Array<LedgerCategoryOrder>,
};

const LedgerAccountsTab = ({
  ledgerAccountsByCategory,
  search,
  isSorting,
  clearFiltersForSorting,
  canSort,
  ledgerCategoryOrders,
}: LedgerAccountsTabProps) => {
  const client = useClient();

  const [featureState] = useFeatureFlags();
  const {
    featureContext: { features },
  } = featureState;

  const [sortByOrderNumber, { loading: loadingSortByOrder }] = useMutation(
    SORT_LEDGER_CATEGORY_BY_ORDER_NUMBER,
    {
      onError: error => {
        toastError(
          'An error occurred sorting these ledger accounts manually',
          showServerError(error),
        );
      },
      awaitRefetchQueries: true,
      refetchQueries: [{ query: GET_LEDGER_CATEGORY_ORDER, variables: { clientId: client.id } }],
    },
  );

  const [sortAlphabetical, { loading: loadingSortAlphabetical }] = useMutation(
    SORT_LEDGER_CATEGORY_ALPHABETICAL,
    {
      onError: error => {
        toastError(
          'An error occurred sorting these ledger accounts alphabetical',
          showServerError(error),
        );
      },
      awaitRefetchQueries: true,
      refetchQueries: [{ query: GET_LEDGER_CATEGORY_ORDER, variables: { clientId: client.id } }],
    },
  );

  const onClickSortCheckbox = (isCategorySortedAlphabetical, ledgerCategory) => {
    const options = { variables: { clientId: client.id, categoryId: ledgerCategory.id } };
    isCategorySortedAlphabetical ? sortByOrderNumber(options) : sortAlphabetical(options);
  };

  return (
    <LedgerAccountsTabLayout>
      {ledgerCategories.map(ledgerCategory => {
        const isPBCategory = ledgerCategory.id === '9';
        const ledgersAccountsForCategory = isPBCategory
          ? // if this is the Personal/Business section, don't show the categorization holding ledger, it should not be edited.
            // $FlowFixMe - optional chaining not supported in flow
            ledgerAccountsByCategory[ledgerCategory.id]?.filter(ledgerAccount => {
              return !(ledgerAccount.code === 9000);
            }) || []
          : ledgerAccountsByCategory[ledgerCategory.id] || [];
        const ledgerCategoryOrderType = ledgerCategoryOrders.find(
          ledgerCategoryOrder => ledgerCategory.id === ledgerCategoryOrder.categoryId,
        )?.type;
        const isCategorySortedAlphabetical =
          ledgerCategoryOrderType && ledgerCategoryOrderType === 'ALPHABETICAL';
        const ledgerCategoryName = isPBCategory
          ? 'Personal Accounts used for Business'
          : ledgerCategory.name;

        return (
          <Fragment key={ledgerCategory.id}>
            <LedgerAccountsTableHeaderLayout>
              <TextBlock styleType="largeBold">
                {`${ledgerCategoryName} (${ledgersAccountsForCategory.length})`}
              </TextBlock>
              {isSorting ? (
                <SortingLayout>
                  <Checkbox
                    aria-label="Is Sorted Alphabetically"
                    onClick={() =>
                      onClickSortCheckbox(isCategorySortedAlphabetical, ledgerCategory)
                    }
                    checkboxState={
                      isCategorySortedAlphabetical
                        ? checkboxStates.CHECKED
                        : checkboxStates.UNCHECKED
                    }
                  />
                  <SortingCheckboxTextLayout>
                    <Text color={colors.cloud} styleType="small" uppercase>
                      Sort Alphabetically
                    </Text>
                  </SortingCheckboxTextLayout>
                </SortingLayout>
              ) : (
                <SortingLayout>
                  <Button
                    disabled={isSorting}
                    styleType="secondary"
                    onClick={clearFiltersForSorting}
                  >
                    Sort Ledger Accounts
                  </Button>
                </SortingLayout>
              )}
            </LedgerAccountsTableHeaderLayout>
            <DndProvider backend={HTML5Backend}>
              {isPBCategory && (
                <PBDescriptionTextLayout>
                  <Text>
                    These holding accounts are not represented in the General Ledger and contain
                    transactions that need to be processed before they can be added as journal
                    entries to Balance Sheet and Income Statement.
                  </Text>
                </PBDescriptionTextLayout>
              )}
              <LedgerAccountsTable
                isLoading={loadingSortAlphabetical || loadingSortByOrder}
                canSort={canSort && !isCategorySortedAlphabetical}
                isSorting={isSorting}
                ledgerCategory={ledgerCategory}
                ledgersAccountsForCategory={ledgersAccountsForCategory}
                isCategorySortedAlphabetical={!!isCategorySortedAlphabetical}
                search={search}
              />
            </DndProvider>
          </Fragment>
        );
      })}
    </LedgerAccountsTabLayout>
  );
};

export default LedgerAccountsTab;
